import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Eye, Star, RefreshCw, Trophy, Play, Share2, Copy, CheckCircle } from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';

const DECISION_LABELS = {
  knockout: { text: 'KNOCKOUT', color: '#EF4444' },
  split_decision: { text: 'SPLIT DECISION', color: '#F59E0B' },
  unanimous: { text: 'UNANIMOUS', color: '#22C55E' },
  draw: { text: 'DRAW', color: '#A0A0A0' },
};

export default function CatFightDetail() {
  const { frayId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [fight, setFight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('fight-card');
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    API.get(`/api/gallery/cat-fights/${frayId}`)
      .then(r => setFight(r.data))
      .catch(() => navigate('/gallery/cat-fights'))
      .finally(() => setLoading(false));
  }, [frayId, navigate]);

  const handleRate = async () => {
    if (!rating || !user) return;
    setSubmittingRating(true);
    try {
      await API.post(`/api/cat-fights/${frayId}/rate`, { stars: rating, review_text: reviewText });
      setRatingSubmitted(true);
      // Refresh to show updated ratings
      const res = await API.get(`/api/gallery/cat-fights/${frayId}`);
      setFight(res.data);
    } catch (err) {
      console.error('Rating failed:', err);
    }
    setSubmittingRating(false);
  };

  const handleRerun = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      // Create a new cat fight with same config
      const voices = (fight.voices || []).map(v => ({
        name: v.name,
        mandate: v.mandate,
        model: v.default_model || 'google/gemini-2.5-flash',
        model_tier: v.model_tier || 'economy',
        color: v.color,
      }));
      const res = await API.post('/api/frays', {
        scenario: fight.scenario,
        debate_mode: 'cat_fight',
        friction_level: 10,
        cat_fight_theme: fight.cat_fight_theme || 'boxing_ring',
        custom_voices: voices,
        num_phases: 1,
      });
      // Increment rerun count on original
      try { await API.put(`/api/frays/${frayId}/publish`, { is_public: true }); } catch {}
      navigate(`/fray/${res.data.id}/reveal`);
    } catch (err) {
      console.error('Re-run failed:', err);
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading || !fight) return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="pt-24 flex justify-center">
        <div className="w-6 h-6 border-2 border-[#EF4444] border-t-transparent rounded-full animate-spin" />
      </div>
    </div>
  );

  const fightCard = fight.fight_card || {};
  const highlights = fight.highlight_reel?.highlights || [];
  const transcript = fight.transcript || [];
  const ratings = fight.ratings || [];
  const dec = DECISION_LABELS[fightCard.decision] || DECISION_LABELS.draw;
  const isOwner = user?.id === fight.user_id;

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="pt-20 pb-12 px-6 max-w-4xl mx-auto">
        <Link to="/gallery/cat-fights" className="flex items-center gap-1 text-xs text-[#A0A0A0] hover:text-[#EF4444] font-mono mb-4">
          <ArrowLeft className="w-3 h-3" /> Gallery
        </Link>

        {/* Fight Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="p-6 bg-[#1A1A1A] border border-[#2A2A2A] mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-xl font-bold font-['Barlow_Condensed'] mb-1">
                  {fight.gallery_title || fight.scenario?.slice(0, 100)}
                </h1>
                <div className="flex items-center gap-3 text-[10px] font-mono text-[#A0A0A0]">
                  <span>by {fight.creator_name}</span>
                  <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {fight.view_count || 0}</span>
                  <span className="flex items-center gap-1"><Star className="w-3 h-3 text-[#F59E0B]" /> {fight.avg_rating ? fight.avg_rating.toFixed(1) : '—'} ({fight.rating_count || 0})</span>
                  <span className="flex items-center gap-1"><RefreshCw className="w-3 h-3" /> {fight.rerun_count || 0} re-runs</span>
                </div>
              </div>
              <span className="text-[10px] px-2 py-1 border font-mono font-bold"
                style={{ color: dec.color, borderColor: dec.color + '40' }}>{dec.text}</span>
            </div>

            {/* Winner */}
            {fightCard.winner && fightCard.winner !== 'Draw' && (
              <div className="text-center py-3 bg-[#EF4444]/5 border border-[#EF4444]/20 mb-4">
                <span className="text-[10px] font-mono text-[#EF4444] block">WINNER</span>
                <span className="text-2xl font-bold font-['Barlow_Condensed']">{fightCard.winner}</span>
              </div>
            )}

            {fightCard.fight_summary && (
              <p className="text-sm text-[#A0A0A0] leading-relaxed mb-4">{fightCard.fight_summary}</p>
            )}

            {/* Actions */}
            <div className="flex gap-2 flex-wrap">
              <button onClick={handleRerun}
                className="px-4 py-2 bg-[#EF4444] text-white text-xs font-medium flex items-center gap-1.5">
                <RefreshCw className="w-3.5 h-3.5" /> Re-run This Fight
              </button>
              <Link to={`/replay/${frayId}`}
                className="px-4 py-2 border border-[#2A2A2A] text-xs text-[#A0A0A0] hover:text-white flex items-center gap-1.5">
                <Play className="w-3.5 h-3.5" /> Watch Replay
              </Link>
              <button onClick={handleShare}
                className="px-4 py-2 border border-[#2A2A2A] text-xs text-[#A0A0A0] hover:text-white flex items-center gap-1.5">
                {copied ? <><CheckCircle className="w-3.5 h-3.5 text-[#22C55E]" /> Copied</> : <><Share2 className="w-3.5 h-3.5" /> Share</>}
              </button>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-[#2A2A2A]">
          {['fight-card', 'highlights', 'transcript', 'ratings'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-2 text-xs font-mono capitalize border-b-2 -mb-px ${
                tab === t ? 'text-[#EF4444] border-[#EF4444]' : 'text-[#A0A0A0] border-transparent'
              }`}>{t}</button>
          ))}
        </div>

        {/* Fight Card Tab */}
        {tab === 'fight-card' && fightCard.voice_scores && (
          <div className="space-y-3">
            {fightCard.voice_scores.map((vs, i) => (
              <div key={i} className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold font-['Barlow_Condensed']">{vs.voice}</span>
                  <span className="text-xs font-mono text-[#A0A0A0]">
                    AGG:{vs.aggression} DEF:{vs.defense} CROWD:{vs.crowd_work}
                  </span>
                </div>
                <div className="flex gap-2 mb-2">
                  {['aggression', 'defense', 'crowd_work'].map(stat => (
                    <div key={stat} className="flex-1">
                      <div className="text-[8px] font-mono text-[#A0A0A0] mb-1 capitalize">{stat.replace('_', ' ')}</div>
                      <div className="h-2 bg-[#2A2A2A] overflow-hidden">
                        <div className="h-full bg-[#EF4444]" style={{ width: `${(vs[stat] || 0) * 10}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
                {vs.best_blow && <p className="text-[10px] text-[#22C55E] font-mono mt-2">Best blow: {vs.best_blow}</p>}
                {vs.weakest_moment && <p className="text-[10px] text-[#EF4444] font-mono">Weak moment: {vs.weakest_moment}</p>}
              </div>
            ))}
            {fightCard.mvp_heckle && (
              <div className="p-4 bg-[#F59E0B]/5 border border-[#F59E0B]/30">
                <span className="text-[10px] font-mono text-[#F59E0B] block mb-1">MVP HECKLE</span>
                <p className="text-sm">"{fightCard.mvp_heckle.text}"</p>
                <p className="text-xs text-[#A0A0A0] mt-1">— {fightCard.mvp_heckle.author}</p>
              </div>
            )}
          </div>
        )}

        {/* Highlights Tab */}
        {tab === 'highlights' && (
          <div className="space-y-4">
            {highlights.length > 0 ? highlights.map((h, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-[#EF4444]">#{h.rank}</span>
                    <span className="text-sm font-semibold font-['Barlow_Condensed']">{h.title}</span>
                  </div>
                  <span className="text-[9px] font-mono text-[#A0A0A0]">{h.voice} · Round {h.round}</span>
                </div>
                <blockquote className="border-l-2 border-[#EF4444] pl-3 text-sm leading-relaxed mb-2">{h.quote}</blockquote>
                <p className="text-[10px] text-[#A0A0A0] font-mono">{h.why}</p>
              </motion.div>
            )) : <p className="text-[#A0A0A0] text-sm">No highlights available.</p>}
          </div>
        )}

        {/* Transcript Tab */}
        {tab === 'transcript' && (
          <div className="space-y-3">
            {transcript.length > 0 ? transcript.map((entry, i) => {
              const voice = (fight.voices || []).find(v => v.name === entry.voice);
              return (
                <div key={i} className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-3 h-3 border-2 shrink-0" style={{ borderColor: voice?.color || '#A0A0A0' }} />
                    <span className="text-xs font-semibold" style={{ color: voice?.color || '#F5F5F5' }}>{entry.voice}</span>
                    <span className="text-[9px] font-mono text-[#A0A0A0]">Round {entry.phase}</span>
                  </div>
                  <p className="text-sm text-[#A0A0A0] leading-relaxed">{entry.content}</p>
                </div>
              );
            }) : <p className="text-[#A0A0A0] text-sm">Transcript not available.</p>}
          </div>
        )}

        {/* Ratings Tab */}
        {tab === 'ratings' && (
          <div>
            {/* Submit rating */}
            {user && !isOwner && !ratingSubmitted && (
              <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] mb-6">
                <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-3">Rate This Fight</h3>
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map(s => (
                    <button key={s} onClick={() => setRating(s)}
                      className={`text-2xl transition-colors ${s <= rating ? 'text-[#F59E0B]' : 'text-[#2A2A2A] hover:text-[#F59E0B]/50'}`}>
                      ★
                    </button>
                  ))}
                  {rating > 0 && <span className="text-xs font-mono text-[#A0A0A0] ml-2">{rating}/5</span>}
                </div>
                <textarea value={reviewText} onChange={e => setReviewText(e.target.value.slice(0, 500))}
                  className="w-full p-3 bg-[#0F0F0F] border border-[#2A2A2A] text-sm resize-none h-20 focus:outline-none focus:border-[#EF4444]"
                  placeholder="Optional review — what made this fight great (or terrible)?" />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[9px] font-mono text-[#A0A0A0]">{reviewText.length}/500</span>
                  <button onClick={handleRate} disabled={!rating || submittingRating}
                    className="px-4 py-1.5 bg-[#F59E0B] text-black text-xs font-medium disabled:opacity-50">
                    {submittingRating ? 'Submitting...' : 'Submit Rating'}
                  </button>
                </div>
              </div>
            )}
            {ratingSubmitted && (
              <div className="p-4 bg-[#22C55E]/5 border border-[#22C55E]/30 mb-6 text-sm text-[#22C55E]">
                Rating submitted! Thanks for your feedback.
              </div>
            )}

            {/* Existing ratings */}
            <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-3">Reviews ({ratings.length})</h3>
            {ratings.length > 0 ? (
              <div className="space-y-2">
                {ratings.map((r, i) => (
                  <div key={r.id || i} className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[#F59E0B] text-sm">{'★'.repeat(r.stars)}{'☆'.repeat(5 - r.stars)}</span>
                        <span className="text-xs font-medium">{r.user_name}</span>
                      </div>
                      <span className="text-[9px] font-mono text-[#A0A0A0]">{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                    {r.review_text && <p className="text-xs text-[#A0A0A0] mt-1">{r.review_text}</p>}
                  </div>
                ))}
              </div>
            ) : <p className="text-[#A0A0A0] text-sm">No reviews yet. Be the first!</p>}
          </div>
        )}
      </div>
    </div>
  );
}
